import { AI_SUGGESTION_TRIGGERED_FROM } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { keydownHandler } from '@atlaskit/editor-prosemirror/keymap';

import type { AIPlugin } from '../../aiPluginType';

import { clearSuggestion, updateLocale } from './commands/commands';
import { suggestionManager } from './handlers/suggestion-manager';
import { inlineSuggestionPluginKey } from './inline-suggestion-plugin-key';
import {
	createInitialState,
	createPluginState,
	getPluginState as getInlineSuggestionState,
} from './plugin-factory';
import {
	createKeyHander,
	docHasChanged,
	fireAnalyticsForNewInlineSuggestionShown,
	localeHasChanged,
	KeydownActionType,
	hasActiveSuggestion,
	isFocusTransitioningToPopup,
	shouldInterceptRightArrowAtDocEnd,
} from './utils';

export const createInlineSuggestionPlugin = (options: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: PMPluginFactoryParams['dispatch'];
	getIntl: PMPluginFactoryParams['getIntl'];
}) => {
	const { dispatch, getIntl, api } = options;
	const { locale } = getIntl();

	return new SafePlugin({
		key: inlineSuggestionPluginKey,
		state: createPluginState(dispatch, createInitialState({ locale })),
		props: {
			decorations: (state) => {
				const { decorationSet } = getInlineSuggestionState(state);
				return decorationSet;
			},
			handleKeyDown: keydownHandler({
				Enter: createKeyHander(
					KeydownActionType.INSERT,
					AI_SUGGESTION_TRIGGERED_FROM.ENTER_BUTTON,
					api,
				),
				Tab: createKeyHander(
					KeydownActionType.INSERT,
					AI_SUGGESTION_TRIGGERED_FROM.TAB_BUTTON,
					api,
				),
				Escape: createKeyHander(
					KeydownActionType.DISMISS,
					AI_SUGGESTION_TRIGGERED_FROM.ESCAPE_BUTTON,
					api,
				),
				ArrowRight: shouldInterceptRightArrowAtDocEnd,
			}),
			handleDOMEvents: {
				blur: (view, event) => {
					if (hasActiveSuggestion(view.state) && !isFocusTransitioningToPopup(event)) {
						clearSuggestion()(view.state, view.dispatch);
						return false;
					}
				},
			},
		},
		view: () => {
			return {
				update: (view, prevState) => {
					try {
						const currentState = view.state;
						if (docHasChanged(prevState, currentState)) {
							suggestionManager.scheduleScanForSuggestions(view);
						}
						const currentLocale = getIntl().locale;
						if (localeHasChanged(currentState, currentLocale)) {
							updateLocale(currentLocale)(currentState, view.dispatch);
						}
					} catch (err) {
						// HOT-118204: swallow uncaught errors for now to prevent collab catchup failures
					}
				},
				destroy: () => {
					suggestionManager.cancelSuggestionScans();
				},
			};
		},
		appendTransaction: (_transactions, oldState, newState) => {
			return fireAnalyticsForNewInlineSuggestionShown(oldState, newState, api);
		},
	});
};
